import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Pencil, Trash2, Globe, ExternalLink } from "lucide-react";
import { toast } from "sonner";

interface LandingPage {
  id: string;
  title: string;
  slug: string;
  meta_title: string | null;
  meta_description: string | null;
  meta_keywords: string | null;
  hero_image_url: string | null;
  content: string;
  is_published: boolean;
  sort_order: number;
}

const emptyForm = {
  title: "",
  slug: "",
  meta_title: "",
  meta_description: "",
  meta_keywords: "",
  hero_image_url: "",
  content: "",
  is_published: false,
  sort_order: 0,
};

function slugify(text: string) {
  return text
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");
}

export function LandingPagesSection() {
  const [pages, setPages] = useState<LandingPage[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState(emptyForm);
  const [autoSlug, setAutoSlug] = useState(true);

  const fetchPages = async () => {
    const { data } = await supabase
      .from("landing_pages")
      .select("*")
      .order("sort_order");
    setPages((data as LandingPage[]) ?? []);
    setLoading(false);
  };

  useEffect(() => { fetchPages(); }, []);

  const handleTitleChange = (title: string) => {
    setForm((f) => ({
      ...f,
      title,
      slug: autoSlug ? slugify(title) : f.slug,
    }));
  };

  const openCreate = () => {
    setEditingId(null);
    setForm(emptyForm);
    setAutoSlug(true);
    setDialogOpen(true);
  };

  const openEdit = (page: LandingPage) => {
    setEditingId(page.id);
    setForm({
      title: page.title,
      slug: page.slug,
      meta_title: page.meta_title ?? "",
      meta_description: page.meta_description ?? "",
      meta_keywords: page.meta_keywords ?? "",
      hero_image_url: page.hero_image_url ?? "",
      content: page.content,
      is_published: page.is_published,
      sort_order: page.sort_order,
    });
    setAutoSlug(false);
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.title.trim() || !form.slug.trim()) {
      toast.error("Title and slug are required");
      return;
    }

    const payload = {
      title: form.title,
      slug: form.slug,
      meta_title: form.meta_title || null,
      meta_description: form.meta_description || null,
      meta_keywords: form.meta_keywords || null,
      hero_image_url: form.hero_image_url || null,
      content: form.content,
      is_published: form.is_published,
      sort_order: form.sort_order,
    };

    if (editingId) {
      const { error } = await supabase.from("landing_pages").update(payload).eq("id", editingId);
      if (error) { toast.error(error.message); return; }
      toast.success("Page updated");
    } else {
      const { error } = await supabase.from("landing_pages").insert(payload);
      if (error) { toast.error(error.message); return; }
      toast.success("Page created");
    }
    setDialogOpen(false);
    fetchPages();
  };

  const handleDelete = async (id: string) => {
    if (!confirm("Delete this page?")) return;
    await supabase.from("landing_pages").delete().eq("id", id);
    toast.success("Deleted");
    fetchPages();
  };

  const togglePublish = async (page: LandingPage) => {
    await supabase.from("landing_pages").update({ is_published: !page.is_published }).eq("id", page.id);
    fetchPages();
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold">Landing Pages</h2>
        <Button onClick={openCreate} size="sm" className="gap-2">
          <Plus className="w-4 h-4" /> New Page
        </Button>
      </div>

      {loading ? (
        <p className="text-muted-foreground">Loading…</p>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Title</TableHead>
              <TableHead>Slug</TableHead>
              <TableHead>Published</TableHead>
              <TableHead>Order</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {pages.map((p) => (
              <TableRow key={p.id}>
                <TableCell className="font-medium">{p.title}</TableCell>
                <TableCell>
                  <a
                    href={`/transfers/${p.slug}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary hover:underline inline-flex items-center gap-1 text-sm"
                  >
                    /transfers/{p.slug} <ExternalLink className="w-3 h-3" />
                  </a>
                </TableCell>
                <TableCell>
                  <Switch checked={p.is_published} onCheckedChange={() => togglePublish(p)} />
                </TableCell>
                <TableCell>{p.sort_order}</TableCell>
                <TableCell className="text-right space-x-2">
                  <Button variant="ghost" size="icon" onClick={() => openEdit(p)}>
                    <Pencil className="w-4 h-4" />
                  </Button>
                  <Button variant="ghost" size="icon" onClick={() => handleDelete(p.id)}>
                    <Trash2 className="w-4 h-4 text-destructive" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingId ? "Edit Page" : "New Landing Page"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Title *</Label>
                <Input value={form.title} onChange={(e) => handleTitleChange(e.target.value)} placeholder="Airport to Bavaro Transfer" />
              </div>
              <div className="space-y-2">
                <Label>Slug *</Label>
                <Input
                  value={form.slug}
                  onChange={(e) => { setAutoSlug(false); setForm((f) => ({ ...f, slug: e.target.value })); }}
                  placeholder="airport-to-bavaro"
                />
                <p className="text-xs text-muted-foreground">/transfers/{form.slug || "…"}</p>
              </div>
            </div>

            <div className="space-y-2">
              <Label>SEO Title</Label>
              <Input value={form.meta_title} onChange={(e) => setForm((f) => ({ ...f, meta_title: e.target.value }))} placeholder="Best Airport Transfer to Bavaro | Smart Punta Cana" />
              <p className="text-xs text-muted-foreground">{(form.meta_title || "").length}/60 chars recommended</p>
            </div>

            <div className="space-y-2">
              <Label>SEO Description</Label>
              <Textarea value={form.meta_description} onChange={(e) => setForm((f) => ({ ...f, meta_description: e.target.value }))} placeholder="Book reliable airport transfers to Bavaro…" rows={2} />
              <p className="text-xs text-muted-foreground">{(form.meta_description || "").length}/160 chars recommended</p>
            </div>

            <div className="space-y-2">
              <Label>SEO Keywords</Label>
              <Input value={form.meta_keywords} onChange={(e) => setForm((f) => ({ ...f, meta_keywords: e.target.value }))} placeholder="punta cana transfer, airport taxi, bavaro shuttle" />
            </div>

            <div className="space-y-2">
              <Label>Hero Image URL</Label>
              <Input value={form.hero_image_url} onChange={(e) => setForm((f) => ({ ...f, hero_image_url: e.target.value }))} placeholder="https://…" />
            </div>

            <div className="space-y-2">
              <Label>Content</Label>
              <Textarea
                value={form.content}
                onChange={(e) => setForm((f) => ({ ...f, content: e.target.value }))}
                placeholder="Write your page content here. Use ## for headings, paragraphs separated by blank lines."
                rows={10}
              />
              <p className="text-xs text-muted-foreground">Use ## for headings. Separate paragraphs with blank lines.</p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Sort Order</Label>
                <Input type="number" value={form.sort_order} onChange={(e) => setForm((f) => ({ ...f, sort_order: parseInt(e.target.value) || 0 }))} />
              </div>
              <div className="flex items-center gap-3 pt-6">
                <Switch checked={form.is_published} onCheckedChange={(v) => setForm((f) => ({ ...f, is_published: v }))} />
                <Label>Published</Label>
              </div>
            </div>

            <div className="flex justify-end gap-3 pt-4">
              <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
              <Button onClick={handleSave}>{editingId ? "Update" : "Create"}</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
